import React, { useCallback } from 'react'
import useFilterContext from 'components/Filter/context'
import useFilterUtils from 'components/Filter/useFilterUtils'
import useHistoryUtils from 'hooks/useHistoryUtils'
import useRolesViews from 'hooks/useRolesViews'
import useStateBackLink from 'hooks/useStateBackLink'
import TaskListHeader from 'redesign/components/organisms/headers/TaskListHeader'
import { TaskList } from 'redesign/components/templates/ContentLists'
import { getTaskUrl } from 'redesign/components/templates/ContentLists/TaskList/utils/getTaskUrl'
import Filters from 'redesign/components/templates/Filters'
import { FilterStateListener } from 'redesign/components/templates/Filters/FilterStateListener'
import Page from 'redesign/components/templates/Page'
import routes from 'services/RoutesProvider/routes'
import type { TaskListElement } from 'shared/graphql/graphql'
import { TASK_LIST_ROOT_ID } from './constants'
import type { TaskListPageContentProps } from './types'
import { useTaskList } from './use-task-list'

const TaskListPageContent: React.FC<TaskListPageContentProps> = ({ configLoading }) => {
  const { isOffice } = useRolesViews()
  const {
    list: tasks,
    loading: taskLoading,
    loadMore,
    showLoadMoreButton,
    handleFilterChange,
    hasFilters,
    showAddTaskButton,
    moreButtonOptions,
  } = useTaskList({
    isOffice,
    isLoadingConfig: configLoading,
  })
  const loading = configLoading || taskLoading
  const { setLoadFilterTasks } = useFilterUtils()
  const { storeState } = useFilterContext()
  const { pushWithRole } = useHistoryUtils()
  const { setBackLink } = useStateBackLink()

  const onTaskClick = useCallback(
    ({ task }: { task: TaskListElement }) => {
      const taskUrl = getTaskUrl({ task })

      setLoadFilterTasks()
      storeState()
      pushWithRole(taskUrl)
      setBackLink(routes.openTasks)
    },
    [pushWithRole, setBackLink, setLoadFilterTasks, storeState],
  )

  return (
    <FilterStateListener onFilterChange={handleFilterChange}>
      <Page
        rootId={TASK_LIST_ROOT_ID}
        header={
          <TaskListHeader
            isOffice={isOffice}
            showAddTaskButton={showAddTaskButton}
            moreButtonOptions={moreButtonOptions}
          />
        }
      >
        <TaskList
          isLoading={loading}
          isOffice={isOffice}
          tasks={tasks}
          onTaskClick={onTaskClick}
          showLoadMoreButton={showLoadMoreButton}
          loadMore={loadMore}
          hasFilters={hasFilters}
        />
      </Page>
      <Filters rootId={TASK_LIST_ROOT_ID} />
    </FilterStateListener>
  )
}

export default TaskListPageContent
